import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { parseUrlVariables } from '../../utils/CommonFunctions.js';
import { login, logout } from '../../actions/Actions.js';
import { SiteLoading } from '../../views/components/SiteLoading.js';
import GoogleAnalytics from '../../utils/GoogleAnalytics.js';

/**
 * OpenId Component - SECURITY WARNING
 *
 * IMPORTANT: This component previously accepted passwords via URL parameters,
 * which is a critical security vulnerability (OWASP A07:2021).
 *
 * BACKGROUND:
 * - Originally implemented to accept email/password via URL parameters
 * - Name "OpenId" is misleading - this is NOT OpenID Connect standard
 * - Not required by QuickBooks Web Connector or QuickBooks Online
 *
 * QUICKBOOKS AUTHENTICATION FACTS:
 * - QuickBooks Web Connector: Uses SOAP/XML authentication, not URL params
 * - QuickBooks Online: Uses OAuth 2.0/OpenID Connect standards
 * - Neither QuickBooks integration requires URL parameter authentication
 *
 * SECURITY ISSUES WITH URL PARAMETERS:
 * - Passwords exposed in browser history
 * - Credentials logged in server access logs
 * - Visible in HTTP referrer headers
 * - Can be cached by proxies and CDNs
 *
 * CURRENT IMPLEMENTATION:
 * - Passwords via URL are now BLOCKED
 * - Only email parameter is accepted for pre-filling login form
 * - Redirects to proper login page for secure authentication
 *
 * @deprecated Consider removing this component entirely in favor of
 *             standard login flows or proper OAuth implementations
 */
class OpenId extends Component {
    constructor(props) {
        super(props);
        this.state = {
            redirectToHome: false,
            error: null,
        };
    }

    componentDidMount() {
        // Handle URL parameters properly in lifecycle method (not constructor)
        const search = this.props.history.location.search;
        if (search !== '') {
            try {
                let parsedVariables = parseUrlVariables(this.props.history);

                // SECURITY VULNERABILITY FIXED:
                // Previously this code accepted both email AND password via URL parameters
                // and performed automatic login. This exposed passwords in:
                // - Browser history
                // - Server logs
                // - HTTP referrer headers
                // - Proxy/CDN caches
                //
                // OLD CODE (REMOVED FOR SECURITY):
                // if ('email' in parsedVariables && 'password' in parsedVariables) {
                //     this.props.login({
                //         email: parsedVariables.email,
                //         password: parsedVariables.password,  // CRITICAL: Never pass passwords in URLs!
                //     });
                // }
                //
                // This pattern is NOT required by QuickBooks integrations:
                // - QuickBooks Web Connector uses SOAP authentication
                // - QuickBooks Online uses OAuth 2.0
                // Neither requires URL parameter authentication

                // SECURITY FIX: Only allow email parameter for pre-filling login form
                // Passwords must NEVER be accepted via URL parameters
                if ('email' in parsedVariables) {
                    // Track analytics if provided
                    if ('new_account' in parsedVariables) {
                        if (parsedVariables.new_account === 'true') {
                            GoogleAnalytics.openId('New Account');
                        } else if (parsedVariables.new_account === 'false') {
                            GoogleAnalytics.openId('Login');
                        }
                    }

                    // Redirect to login page with email pre-filled
                    // This is secure as it only passes non-sensitive data
                    this.props.history.push(
                        `/login?email=${encodeURIComponent(parsedVariables.email)}`
                    );
                    return;
                }
            } catch (error) {
                console.error('Error parsing URL parameters:', error);
                this.setState({ error: 'Invalid URL parameters' });
                // Redirect to login after showing error
                setTimeout(() => {
                    this.props.history.push('/login');
                }, 3000);
            }
        } else {
            this.props.history.push('/home');
        }
    }

    componentDidUpdate(prevProps) {
        // Resources have now loaded, proceed with redirect
        if (this.props.isLoggedIn && this.props.resourcesLoaded && !prevProps.resourcesLoaded) {
            this.setState({
                redirectToHome: true,
            });
        }
    }

    render() {
        const { redirectToHome, error } = this.state;

        if (redirectToHome === true) {
            return <Redirect to="/" />;
        }

        if (error) {
            return (
                <div className="openIdContainer">
                    <div className="error-message">
                        <h3>Authentication Error</h3>
                        <p>{error}</p>
                        <p>Redirecting to login page...</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="openIdContainer">
                <SiteLoading />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        isLoggedIn: !_.isNil(state.appStore.session_id),
        resourcesLoaded: state.businessResourcesStore.resourcesLoaded,
    };
}

const mapDispatchToState = {
    login,
    logout,
};

export default connect(
    mapStateToProps,
    mapDispatchToState
)(OpenId);
